<template>
  <!-- Zahlungsmethoden-Pflicht (Feedback 13.10.26): Administratoren von
       Unternehmen mit kostenpflichtigen Apps ohne Zahlungsmethode müssen sie
       nachholen – der Dialog lässt sich nicht schließen. Mitarbeitende
       sehen ihn nicht. -->
  <PaymentMethodDialog
    v-if="showDialog"
    :intro="$gt('Ihr Unternehmen nutzt kostenpflichtige Apps. Bitte hinterlegen Sie eine Zahlungsmethode, damit die Abrechnung nach der kostenfreien Phase starten kann. Bis dahin wird nichts abgebucht.')"
    :notice="errorMessage"
    required
    @close="onDismissed"
    @updated="onUpdated"
  />
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import PaymentMethodDialog from '@/pages/pageInvoices/PaymentMethodDialog.vue'
  import { notifyCheckoutOutcome } from '@/services/checkoutOutcomeNotice'
  import { checkoutCompanyFromQuery, checkoutSessionFromQuery, settlePaymentRequirement } from '@/services/paymentRequirement'
  import { useAuthStore } from '@/stores/authStore'

  // Höchstens alle 5 Minuten neu prüfen – jede Prüfung fragt Stripe.
  const RECHECK_INTERVAL_MS = 5 * 60 * 1000

  const route = useRoute()
  const router = useRouter()
  const authStore = useAuthStore()

  const required = ref(false)
  const errorMessage = ref('')
  let lastCheckAt = 0
  let checking = false

  // Nicht im Onboarding (eigener Schritt), nicht auf öffentlichen Seiten,
  // nicht während ein Super-Admin ein Unternehmen ansieht und nicht, während
  // die Rechnungsseite gerade die PayPal-Rückkehr bestätigt.
  // Nicht im Schritt „Abrechnung einrichten“ (Spec 2026-10-21): Er klärt
  // Zahlungsmittel und Checkout-Rückkehr des Unternehmens selbst.
  const active = computed(() =>
    authStore.isAdministrator
    && !authStore.isImpersonating
    && route.meta.requiresAuth !== false
    && !route.path.startsWith('/onboarding')
    && route.query.setup !== 'done'
    && route.query.abschnitt !== 'abrechnung',
  )

  const showDialog = computed(() => active.value && required.value)

  async function check (force = false): Promise<void> {
    if (!active.value || checking) return
    if (!force && Date.now() - lastCheckAt < RECHECK_INTERVAL_MS) return
    checking = true
    // Rückkehr aus dem Setup-Checkout der Einstellungen
    // (/organisation?tab=apps&checkout_done=1&checkout_session=cs_…):
    // Zahlungsmethode übernehmen und Abos anlegen. Die Parameter verschwinden
    // sofort aus der Adresse, damit ein Fehlschlag nicht bei jeder Prüfung
    // wiederholt wird – der normale Abgleich holt fehlende Abos nach.
    const checkoutSession = checkoutSessionFromQuery(route.query.checkout_session)
    // Rückkehr in die Benutzerverwaltung eines Unternehmens (Spec
    // 2026-10-22): Der Checkout lief im Scope dieses Unternehmens.
    const checkoutCompany = checkoutSession ? checkoutCompanyFromQuery(route.query) : null
    if (checkoutSession) {
      const { checkout_done: _done, checkout_session: _session, ...query } = route.query
      void router.replace({ query })
    }
    try {
      const requirement = await settlePaymentRequirement(checkoutSession || undefined, checkoutCompany)
      required.value = requirement.required
      errorMessage.value = ''
      lastCheckAt = Date.now()
      // Unternehmen mit eigener Abrechnung, die die Buchung übersprungen
      // hat, nicht still verlieren (Spec 2026-10-21).
      void notifyCheckoutOutcome(requirement.outcome)
    } catch (error) {
      // Prüfung gestört: niemanden blockieren, beim nächsten Seitenwechsel
      // erneut versuchen. Ist der Dialog schon offen, Grund dort anzeigen.
      console.warn('[PaymentRequiredGate] Zahlungsstatus nicht prüfbar', error)
      if (required.value) {
        errorMessage.value = (error as { message?: string })?.message ?? ''
      }
    } finally {
      checking = false
    }
  }

  async function onUpdated (): Promise<void> {
    // Zahlungsmethode gespeichert → fehlende Abos anlegen, Stand neu lesen.
    await check(true)
  }

  function onDismissed (): void {
    // Das Zahlungsformular ließ sich nicht aufbauen (Backend/Stripe) und der
    // Administrator hat „Später erneut versuchen" gewählt: nicht aussperren –
    // der nächste Abgleich (Seitenwechsel nach RECHECK_INTERVAL_MS) zeigt
    // den Dialog wieder, solange die Zahlungsmethode fehlt.
    required.value = false
  }

  watch(active, isActive => {
    if (isActive) void check(true)
  }, { immediate: true })

  watch(() => route.fullPath, () => {
    void check()
  })
</script>

<script lang="ts">
  export default { name: 'PaymentRequiredGate' }
</script>
